import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Grid,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PaidIcon from "@mui/icons-material/Paid";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { axiosConfig } from "../../axios/axiosConfig";
import toast from "react-hot-toast";
import MarketingSpendTable from "./MarketingSpendTable";

const defaultForm = {
  date: "",
  amount: "",
  campaign_name: "",
  description: "",
  channel: "",
};

const MarketingSpend = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [appliedFrom, setAppliedFrom] = useState("");
  const [appliedTo, setAppliedTo] = useState("");

  useEffect(() => {
    const fetchChannels = async () => {
      setChannelsLoading(true);
      try {
        const res = await axiosConfig({
          method: "get",
          url: "/advertisement/marketing-channel/",
        });
        setChannels(res.data);
      } catch (err) {
        console.error("Failed to fetch channels:", err);
      } finally {
        setChannelsLoading(false);
      }
    };
    fetchChannels();
  }, []);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const isValid =
    form.date && form.amount&& form.channel;

  const handleSubmit = async () => {
    if (!isValid) return;
    setLoading(true);
    try {
      await axiosConfig({
        method: "post",
        url: "/advertisement/expense-data/",
        data: {
          date: form.date,
          amount: form.amount,
          campaign_name: form.campaign_name,
          description: form.description || null,
          channel: form.channel,
        },
      });
      toast.success("Marketing spend added successfully!");
      setForm(defaultForm);
      setOpen(false);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      const errMsg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        "Failed to add spend";
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    setAppliedFrom(fromDate);
    setAppliedTo(toDate);
  };

  const handleClose = () => {
    setForm(defaultForm);
    setOpen(false);
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: 13 },
  };

  return (
    <Box sx={{ background: "#f1f5f9", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: "#fff",
          borderBottom: "1px solid #e2e8f0",
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              background: "#04343a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PaidIcon sx={{ color: "#fff", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 800,
                color: "#0f172a",
                lineHeight: 1.2,
              }}
            >
              Marketing Spend
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
              Track your marketing expenditure
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <TextField
            type="date" size="small" label="From" value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: 13, background: "#fff" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" } }}
          />
          <TextField
            type="date" size="small" label="To" value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: 13, background: "#fff" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" } }}
          />
          <Button
            variant="contained" size="small"
            startIcon={<FilterAltIcon sx={{ fontSize: 16 }} />}
            onClick={handleFilter}
            sx={{ height: 36, borderRadius: "8px", background: "#04343a", textTransform: "none", fontSize: 13, fontWeight: 600, px: 2, "&:hover": { background: "#016551" } }}
          >
            Filter
          </Button>
          <Button
            variant="contained" size="small"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={() => setOpen(true)}
            sx={{ height: 36, borderRadius: "8px", background: "#016551", textTransform: "none", fontSize: 13, fontWeight: 600, px: 2, boxShadow: "0 4px 12px rgba(1,101,81,0.3)", "&:hover": { background: "linear-gradient(135deg,#1db954,#17a349)" } }}
          >
            Add Spend
          </Button>
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        <MarketingSpendTable refreshKey={refreshKey} appliedFrom={appliedFrom} appliedTo={appliedTo} />
      </Box>

      {/* Add Spend Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { borderRadius: "14px", width: 480 } }}
      >
        <DialogTitle
          sx={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0f172a",
            borderBottom: "1px solid #f1f5f9",
            pb: 1.5,
          }}
        >
          Add Marketing Spend
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5, pb: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                name="date"
                label="Date"
                value={form.date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                name="amount"
                label="Amount (₹)"
                placeholder="e.g. 5000"
                value={form.amount}
                onChange={handleChange}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                name="campaign_name"
                label="Campaign Name"
                placeholder="Enter campaign name"
                value={form.campaign_name}
                onChange={handleChange}
                sx={fieldSx}
              />
            </Grid>
            {/* Channel Dropdown */}
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                size="small"
                name="channel"
                label="Channel"
                value={form.channel}
                onChange={handleChange}
                sx={fieldSx}
                disabled={channelsLoading}
                InputProps={{
                  endAdornment: channelsLoading ? (
                    <CircularProgress
                      size={14}
                      sx={{ mr: 2, color: "#94a3b8" }}
                    />
                  ) : null,
                }}
              >
                {channels.map((ch) => (
                  <MenuItem key={ch.id} value={ch.id}>
                    {ch.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={3}
                name="description"
                label="Description"
                placeholder="Brief description of the spend..."
                value={form.description}
                onChange={handleChange}
                sx={fieldSx}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={handleClose}
            size="small"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              color: "#64748b",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={!isValid || loading}
            startIcon={
              loading ? (
                <CircularProgress size={14} sx={{ color: "#fff" }} />
              ) : null
            }
            sx={{
              borderRadius: "8px",
              background: "#016551",
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
              boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
              "&:hover": {
                background: "linear-gradient(135deg,#1db954,#17a349)",
              },
            }}
          >
            {loading ? "Submitting…" : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MarketingSpend;
